import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, RefreshCw } from "lucide-react"

interface ResyncConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceName: string
  onConfirm: () => void
  isResyncing?: boolean
  confirmButtonText?: string
  cancelButtonText?: string
}

export function ResyncConfirmationDialog({
  open,
  onOpenChange,
  resourceName,
  onConfirm,
  isResyncing = false,
  confirmButtonText = "Resync Resource",
  cancelButtonText = "Cancel",
}: ResyncConfirmationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div>
              <DialogTitle>Resync resource</DialogTitle>
              <DialogDescription className="mt-1">
                This will resync the resource &quot;{resourceName}&quot; from the source.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg p-4">
            <div className="flex items-start gap-3">
              <RefreshCw className="mt-0.5 h-4 w-4 text-gray-700 dark:text-gray-300" />
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">What changes</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  We match existing tables and columns and update schema-derived details (types, nullability, keys,
                  defaults, and similar metadata). <br /> Your display names, visibility, semantic types, validation
                  rules, and other field settings are kept.
                </p>

                <p className="mt-5 text-sm text-gray-700 dark:text-gray-300">
                  NB: Renamed tables or columns in the database appear as removed plus added. Treat those like a mapping
                  change if you need to carry settings over. Tables or columns removed from the source disappear from
                  your synced schema here. Saved queries, board widgets, or charts still point at whatever you saved.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isResyncing}>
            {cancelButtonText}
          </Button>
          <Button variant="default" onClick={onConfirm} disabled={isResyncing}>
            {isResyncing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resyncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {confirmButtonText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
